import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/apiClient";
import AOS from "aos";
import "aos/dist/aos.css";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useCart } from "./CartContext";
import { toast } from "react-toastify";

const Home = () => {
  const [produits, setProduits] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [newProducts, setNewProducts] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("cat1");
  const [loading, setLoading] = useState({
    categories: true,
    products: true,
    overall: true,
  });
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({
    categories: null,
    products: null,
  });

  // Get addToCart function from CartContext at the top level
  const { addToCart } = useCart();

  // Enhanced error handler
  const handleError = useCallback((error, type) => {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      `Erreur lors du chargement des ${type}`;

    setErrors((prev) => ({
      ...prev,
      [type]: errorMessage,
    }));

    // Show toast notification for major errors
    if (error.response?.status >= 500) {
      toast.error(`Problème de connexion au serveur`, {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  }, []);

  // Enhanced data fetching with better error handling
  const fetchCategories = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, categories: true }));
      setErrors((prev) => ({ ...prev, categories: null }));

      const response = await apiClient.get(`/categories`, {
        timeout: 10000,
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        throw new Error("Format de données invalide pour les catégories");
      }
    } catch (error) {
      handleError(error, "categories");
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  }, [handleError]);

  const fetchProduits = useCallback(async () => {
    try {
      setLoading((prev) => ({ ...prev, products: true }));
      setErrors((prev) => ({ ...prev, products: null }));

      const response = await apiClient.get(`/produits`, {
        timeout: 10000,
        headers: {
          "Cache-Control": "no-cache",
        },
      });

      if (response.data && Array.isArray(response.data)) {
        const allProducts = response.data;
        setProduits(allProducts);
        setBestSellers(allProducts.slice(0, 8));
        setNewProducts(allProducts.slice(8, 16));
        setSaleProducts(allProducts.slice(16, 24));
      } else {
        throw new Error("Format de données invalide pour les produits");
      }
    } catch (error) {
      handleError(error, "products");
    } finally {
      setLoading((prev) => ({ ...prev, products: false }));
    }
  }, [handleError]);

  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
    });

    // Fetch data
    const fetchData = async () => {
      try {
        setLoading((prev) => ({ ...prev, overall: true }));
        await Promise.all([fetchCategories(), fetchProduits()]);
      } catch (error) {
      } finally {
        setLoading((prev) => ({ ...prev, overall: false }));
      }
    };

    fetchData();
  }, [fetchCategories, fetchProduits]);

  // Loading component
  const LoadingSpinner = ({ message = "Chargement..." }) => (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Chargement...</span>
      </div>
      <p className="mt-2 text-muted">{message}</p>
    </div>
  );

  // Error component
  const ErrorMessage = ({ message, onRetry }) => (
    <div className="text-center py-5">
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {message}
      </div>
      {onRetry && (
        <button className="btn btn-primary mt-3" onClick={onRetry}>
          <i className="fas fa-redo me-2"></i>
          Réessayer
        </button>
      )}
    </div>
  );

  // Configuration for sliders
  const heroSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    vertical: true,
    verticalSwiping: true,
    pauseOnHover: true,
  };

  const categorySliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 6,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  // Fixed: Handle add to cart directly in the component, not in a nested function
  const handleAddToCart = (produit) => {
    try {
      addToCart({
        id_prod: produit.id_prod,
        nom_prod: produit.nom_prod,
        prix: produit.prix,
        images: produit.images,
      });

      toast.success(
        <div>
          <strong>{produit.nom_prod}</strong>
          <div>ajouté au panier ✅</div>
        </div>,
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
        },
      );
    } catch (error) {
      toast.error("Erreur lors de l'ajout au panier", {
        position: "bottom-right",
      });
    }
  };

  // Enhanced product section renderer
  const renderProductSection = (products, title) => {
    if (loading.products) {
      return (
        <LoadingSpinner message={`Chargement des ${title.toLowerCase()}...`} />
      );
    }

    if (errors.products) {
      return <ErrorMessage message={errors.products} onRetry={fetchProduits} />;
    }

    if (!products.length) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-box-open fa-3x text-muted mb-3"></i>
          <p className="text-muted">
            Aucun produit disponible dans cette catégorie
          </p>
        </div>
      );
    }

    return (
      <div className="row justify-content-center">
        {products.map((produit) => (
          <div key={produit.id_prod} className="col-xl-3 col-lg-4 col-sm-6">
            <div className="product-item style-one mb-40">
              <div className="product-thumbnail">
                <img
                  src={
                    produit.images?.[0]
                      ? `${process.env.REACT_APP_API_URL}/${produit.images[0]}`
                      : "assets/images/products/feature-product-1.png"
                  }
                  alt={produit.nom_prod}
                  className="product-image"
                  onError={(e) => {
                    e.target.src =
                      "assets/images/products/feature-product-1.png";
                  }}
                />
                <div className="hover-content">
                  <Link to="#" className="icon-btn">
                    <i className="fa fa-heart"></i>
                  </Link>
                  <Link
                    to={
                      produit.images?.[0]
                        ? `${process.env.REACT_APP_API_URL}/${produit.images[0]}`
                        : "#"
                    }
                    className="img-popup icon-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fa fa-eye"></i>
                  </Link>
                </div>
                <div className="cart-button">
                  <button
                    className="cart-btn"
                    onClick={() => handleAddToCart(produit)}
                  >
                    <i className="far fa-shopping-basket"></i>
                    <span className="text">Ajouter au panier</span>
                  </button>
                </div>
              </div>
              <div className="product-info-wrap">
                <div className="product-info">
                  <h4 className="title">
                    <Link to={`/produitsDetails/${produit.id_prod}`}>
                      {produit.nom_prod || "Produit sans nom"}
                    </Link>
                  </h4>
                  {produit.prix && (
                    <div className="price">
                      <span className="current-price">{produit.prix} €</span>
                      {produit.ancien_prix && (
                        <span className="old-price">
                          {produit.ancien_prix} €
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Main loading state
  if (loading.overall && (loading.categories || loading.products)) {
    return (
      <div className="min-vh-50 d-flex justify-content-center align-items-center">
        <LoadingSpinner message="Chargement de l'accueil.." />
      </div>
    );
  }

  return (
    <>
      <main className="main-bg">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-wrapper-one">
            <div className="container">
              <div className="hero-dots"></div>
              <div className="hero-slider-one">
                <Slider {...heroSliderSettings}>
                  {/* Slide 1 */}
                  <div className="single-hero-slider">
                    <div className="row align-items-center">
                      <div className="col-lg-6">
                        <div className="hero-content style-one mb-50">
                          <span className="sub-heading">
                            Accélérez votre transformation digitale
                          </span>
                          <h1>
                            <span>L’excellence technologique</span>
                            <br /> au service de votre croissance
                          </h1>
                          <p>
                            Nous fournissons des équipements technologiques de
                            pointe, adaptés aux besoins des chaque client, pour
                            accompagner durablement votre croissance.
                          </p>
                          <ul>
                            <li>
                              <img alt="" src="assets/images/hero/line-1.png" />
                            </li>
                            <li>
                              <Link
                                to="https://www.dvelgegroup.com"
                                className="theme-btn style-one"
                              >
                                Démarrez votre projet
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="hero-image-box">
                          <div className="hero-image">
                            <img
                              src="assets/images/hero/hero-one_img1.png"
                              alt="Solutions technologiques innovantes"
                              onError={(e) => {
                                e.target.src = "assets/images/placeholder.jpg";
                              }}
                            />
                            <div
                              className="hero-shape bg_cover"
                              style={{
                                backgroundImage: `url(assets/images/hero/hero-one-shape1.png)`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Slider>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="features-section pt-130">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="features-wrapper aos-init"
                  data-aos="fade-up"
                  data-aos-delay="10"
                  data-aos-duration="1000"
                >
                  <div className="iconic-box-item icon-left-box mb-25">
                    <div className="icon">
                      <i className="fas fa-shipping-fast"></i>
                    </div>
                    <div className="content">
                      <h5>Livraison fiable et rapide</h5>
                      <p>
                        Nous assurons une livraison rapide et sécurisée de vos
                        équipements partout en Guinée.
                      </p>
                    </div>
                  </div>

                  <div className="divider mb-25">
                    <img src="assets/images/divider.png" alt="divider" />
                  </div>

                  <div className="iconic-box-item icon-left-box mb-25">
                    <div className="icon">
                      <i className="fas fa-microphone"></i>
                    </div>
                    <div className="content">
                      <h5>Assistance technique dédiée</h5>
                      <p>
                        Notre équipe est disponible pour vous conseiller et vous
                        accompagner à chaque étape.
                      </p>
                    </div>
                  </div>

                  <div className="divider mb-25">
                    <img src="assets/images/divider.png" alt="divider" />
                  </div>

                  <div className="iconic-box-item icon-left-box mb-25">
                    <div className="icon">
                      <i className="far fa-handshake"></i>
                    </div>
                    <div className="content">
                      <h5>Satisfaction garantie</h5>
                      <p>
                        Nous nous engageons à remplacer ou reprendre tout
                        article non conforme à vos attentes.
                      </p>
                    </div>
                  </div>

                  <div className="divider mb-25">
                    <img src="assets/images/divider.png" alt="divider" />
                  </div>

                  <div className="iconic-box-item icon-left-box mb-25">
                    <div className="icon">
                      <i className="fas fa-sack-dollar"></i>
                    </div>
                    <div className="content">
                      <h5>Paiement sécurisé</h5>
                      <p>
                        Effectuez vos achats en toute confiance grâce à nos
                        solutions de paiement sécurisées.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="category-section pt-125 overflow-hidden">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 col-md-8">
                <div className="section-title mb-50">
                  <div className="sub-heading d-inline-flex align-items-center">
                    <i className="flaticon-sparkler"></i>
                    <span className="sub-title">Catégories</span>
                  </div>
                  <h2>Parcourir les catégories</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="category-slider-one">
            {loading.categories ? (
              <LoadingSpinner message="Chargement des catégories..." />
            ) : errors.categories ? (
              <ErrorMessage
                message={errors.categories}
                onRetry={fetchCategories}
              />
            ) : categories.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                <p className="text-muted">Aucune catégorie disponible</p>
              </div>
            ) : (
              <Slider {...categorySliderSettings}>
                {categories.map((category) => (
                  <div
                    key={category.id_cat}
                    className="category-item style-one text-center"
                  >
                    <div className="category-img">
                      <img
                        src={`${process.env.REACT_APP_API_URL}/${category.image_path}`}
                        alt={category.nom_cat}
                        onError={(e) => {
                          e.target.src = "assets/images/placeholder.jpg";
                        }}
                      />
                    </div>
                    <div className="category-content">
                      <Link
                        to={`/produits?category=${category.id_cat}`}
                        className="category-btn"
                      >
                        {category.nom_cat}
                      </Link>
                    </div>
                  </div>
                ))}
              </Slider>
            )}
          </div>
        </section>

        <section class="banner-section pt-130">
          <div class="container">
            <div class="row">
              <div class="col-lg-6">
                <div
                  class="banner-item style-one bg-one mb-40 aos-init aos-animate"
                  data-aos="fade-up"
                  data-aos-delay="10"
                  data-aos-duration="900"
                >
                  <div class="banner-img">
                    <img
                      src="assets/images/banner/banner-1.png"
                      alt="banner image"
                    />
                  </div>
                  <div class="banner-content">
                    <span>
                      Jusqu’à <span class="off">80%</span>
                    </span>
                    <h4>Conçu pour votre quotidien</h4>
                    <Link to="/produits" class="theme-btn style-one">
                      Découvrez nos produits
                    </Link>
                  </div>
                </div>
              </div>
              <div class="col-lg-6">
                <div
                  class="banner-item style-one bg-two mb-40 aos-init aos-animate"
                  data-aos="fade-up"
                  data-aos-delay="20"
                  data-aos-duration="1100"
                >
                  <div class="banner-img">
                    <img
                      src="assets/images/banner/banner-2.png"
                      alt="banner image"
                    />
                  </div>
                  <div class="banner-content">
                    <span>
                      Jusqu’à <span class="off">35%</span>
                    </span>
                    <h4>Le pilier de vos opérations quotidiennes</h4>
                    <Link to="/produits" class="theme-btn style-one">
                      Découvrez nos produits
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Products Section */}
        <section className="features-products pt-90">
          <div className="container">
            <div className="row">
              <div className="col-lg-6">
                <div
                  className="section-title mb-50 text-center text-lg-start"
                  data-aos="fade-right"
                  data-aos-delay="10"
                  data-aos-duration="1000"
                >
                  <div className="sub-heading d-inline-flex align-items-center">
                    <i className="flaticon-sparkler"></i>
                    <span className="sub-title">Produits en Vedette</span>
                  </div>
                  <h2>Notre Collection en Vedette</h2>
                </div>
              </div>
              <div className="col-lg-6">
                <div
                  className="pesco-tabs style-one mb-50"
                  data-aos="fade-left"
                  data-aos-delay="15"
                  data-aos-duration="1200"
                >
                  <ul className="nav nav-tabs" role="tablist">
                    <li>
                      <button
                        className={`nav-link ${
                          activeTab === "cat1" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("cat1")}
                      >
                        Meilleures Ventes
                      </button>
                    </li>
                    <li>
                      <button
                        className={`nav-link ${
                          activeTab === "cat2" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("cat2")}
                      >
                        Nouveautés
                      </button>
                    </li>
                    <li>
                      <button
                        className={`nav-link ${
                          activeTab === "cat3" ? "active" : ""
                        }`}
                        onClick={() => setActiveTab("cat3")}
                      >
                        Promotions
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <div
                  className="tab-content"
                  data-aos="fade-up"
                  data-aos-duration="1200"
                >
                  <div
                    className={`tab-pane fade ${
                      activeTab === "cat1" ? "show active" : ""
                    }`}
                    id="cat1"
                  >
                    {renderProductSection(bestSellers, "Meilleures Ventes")}
                  </div>

                  <div
                    className={`tab-pane fade ${
                      activeTab === "cat2" ? "show active" : ""
                    }`}
                    id="cat2"
                  >
                    {renderProductSection(newProducts, "Nouveautés")}
                  </div>

                  <div
                    className={`tab-pane fade ${
                      activeTab === "cat3" ? "show active" : ""
                    }`}
                    id="cat3"
                  >
                    {renderProductSection(saleProducts, "Promotions")}
                  </div>
                </div>
              </div>
            </div>

            {/* Pagination */}
            {produits.length > 12 && !loading.products && !errors.products && (
              <div className="row">
                <div className="col-lg-12">
                  <div
                    className="pesco-pagination mb-40"
                    data-aos="fade-up"
                    data-aos-delay="70"
                    data-aos-duration="2200"
                  >
                    <ul>
                      <li>
                        <Link to="#">
                          <i className="far fa-angle-left"></i>
                        </Link>
                      </li>
                      <li>
                        <Link to="#">01</Link>
                      </li>
                      <li>
                        <Link to="#">02</Link>
                      </li>
                      <li>
                        <Link to="#">03</Link>
                      </li>
                      <li>
                        <Link to="#">
                          <i className="far fa-angle-right"></i>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Work Processing Section */}
        <section className="work-processing-section pt-30 pb-90">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="section-title text-center mb-60"
                  data-aos="fade-up"
                  data-aos-delay="10"
                  data-aos-duration="800"
                >
                  <div className="sub-heading d-inline-flex align-items-center">
                    <i className="flaticon-sparkler"></i>
                    <span className="sub-title">
                      Simple, transparent, fiable
                    </span>
                    <i className="flaticon-sparkler"></i>
                  </div>
                  <h2>Comment ça marche</h2>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-xl-3 col-sm-6">
                <div
                  className="iconic-box-item style-two mb-40"
                  data-aos="fade-up"
                  data-aos-duration="1000"
                >
                  <div className="sn-number">01</div>
                  <div className="icon">
                    <i className="flaticon-searching"></i>
                  </div>
                  <div className="content">
                    <h6>Parcourir et choisir</h6>
                    <p>
                      Découvrez notre catalogue en ligne et sélectionnez les
                      produits ou équipements dont vous avez besoin.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6">
                <div
                  className="iconic-box-item style-two mb-40"
                  data-aos="fade-up"
                  data-aos-duration="1200"
                >
                  <div className="sn-number">02</div>
                  <div className="icon">
                    <i className="flaticon-payment-method"></i>
                  </div>
                  <div className="content">
                    <h6>Envoyer une demande</h6>
                    <p>
                      Ajoutez les articles à votre sélection et soumettez une
                      demande de devis ou de commande en ligne.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6">
                <div
                  className="iconic-box-item style-two mb-40"
                  data-aos="fade-up"
                  data-aos-duration="1400"
                >
                  <div className="sn-number">03</div>
                  <div className="icon">
                    <i className="flaticon-currency"></i>
                  </div>
                  <div className="content">
                    <h6>Traitement de la commande</h6>
                    <p>
                      Notre équipe analyse votre demande et prépare votre
                      commande avec soin.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6">
                <div
                  className="iconic-box-item style-two mb-40"
                  data-aos="fade-up"
                  data-aos-duration="1600"
                >
                  <div className="sn-number">04</div>
                  <div className="icon">
                    <i className="flaticon-delivery"></i>
                  </div>
                  <div className="content">
                    <h6>Livraison et réception</h6>
                    <p>
                      Votre commande est soigneusement emballée et livrée à
                      votre adresse dans les meilleurs délais.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Best Deal Section */}
        <section className="best-deal-section">
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div
                  className="offer-deal-wrapper bg_cover"
                  data-aos="fade-up"
                  data-aos-duration="1400"
                  style={{
                    backgroundImage: `url(assets/images/bg/deal-bg-1.png)`,
                  }}
                >
                  <div className="deal-img">
                    <span>
                      <img
                        src="assets/images/banner/deal-1.png"
                        alt="Offre spéciale"
                        onError={(e) => {
                          e.target.src = "assets/images/placeholder.jpg";
                        }}
                      />
                    </span>
                  </div>
                  <div className="deal-content">
                    <span class="sub-heading">
                      <i class="fas fa-tags"></i>Offre du Mois
                    </span>
                    <h2>Découvrez l’offre POC</h2>
                    <div class="simply-countdown mb-60">
                      <div class="simply-section simply-days-section">
                        <div>
                          <span class="simply-amount">0</span>
                          <span class="simply-word">day</span>
                        </div>
                      </div>
                      <div class="simply-section simply-hours-section">
                        <div>
                          <span class="simply-amount">0</span>
                          <span class="simply-word">hour</span>
                        </div>
                      </div>
                      <div class="simply-section simply-minutes-section">
                        <div>
                          <span class="simply-amount">0</span>
                          <span class="simply-word">minute</span>
                        </div>
                      </div>
                      <div class="simply-section simply-seconds-section">
                        <div>
                          <span class="simply-amount">0</span>
                          <span class="simply-word">second</span>
                        </div>
                      </div>
                    </div>
                    <div className="simply-countdown mb-60"></div>
                    <div className="shop-button">
                      <Link to="/produits" className="theme-btn style-one">
                        Voir les produits
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Home;
